import { useState } from "react";
import { reviews } from "@/mocks/reviews";
import { useScrollFadeIn } from "@/hooks/useScrollFadeIn";

type Review = typeof reviews[0];

function getAgeStyle(age: string): { bg: string; text: string } {
  if (age.startsWith("20")) return { bg: "bg-teal-100", text: "text-teal-600" };
  if (age.startsWith("30")) return { bg: "bg-amber-300", text: "text-amber-700" };
  if (age.startsWith("40")) return { bg: "bg-rose-300", text: "text-rose-700" };
  if (age.startsWith("50")) return { bg: "bg-slate-200", text: "text-slate-500" };
  return { bg: "bg-stone-100", text: "text-stone-600" };
}

function getGenderIcon(gender: string): string {
  return gender === "female" ? "ri-user-heart-line" : "ri-user-line";
}

const BADGE_PRESETS: Record<string, { icon: string; color: string }> = {
  star: { icon: "ri-star-line", color: "text-amber-500" },
  fire: { icon: "ri-fire-line", color: "text-orange-400" },
  crown: { icon: "ri-vip-crown-line", color: "text-yellow-500" },
};

function getSessionsBadge(sessions: number, badgeOverride?: string): { icon: string; color: string; label: string } | null {
  if (badgeOverride && BADGE_PRESETS[badgeOverride]) {
    return { ...BADGE_PRESETS[badgeOverride], label: "" };
  }
  if (sessions === 0 || sessions === 1) return { icon: "ri-star-line", color: "text-amber-500", label: "初回" };
  if (sessions >= 20) return { icon: "ri-vip-crown-line", color: "text-yellow-500", label: `${sessions}回目` };
  if (sessions >= 5) return { icon: "ri-fire-line", color: "text-orange-400", label: `${sessions}回目` };
  return null;
}

function ReviewCard({ review }: { review: Review }) {
  const [expanded, setExpanded] = useState(false);
  const isLong = review.text.length > 70;
  const ageStyle = getAgeStyle(review.age);
  const genderIcon = getGenderIcon(review.gender);
  const badge = getSessionsBadge(review.sessions, "badge" in review ? (review as Review & { badge?: string }).badge : undefined);

  return (
    <div className="bg-white border border-stone-200 p-3.5 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className={`w-7 h-7 flex items-center justify-center ${ageStyle.bg} rounded-full flex-shrink-0`}>
          <i className={`${genderIcon} ${ageStyle.text} text-sm`}></i>
        </div>
        <p className="text-xs text-stone-700 tracking-wider flex-1">{"label" in review && review.label ? String(review.label) : `${review.age} · ${review.sessions}回目`}</p>
        {badge && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <i className={`${badge.icon} ${badge.color} text-xs`}></i>
          </div>
        )}
      </div>
      <div className="text-xs text-stone-700 leading-relaxed whitespace-pre-line">
        {isLong && !expanded ? `${review.text.slice(0, 70)}…` : review.text}
      </div>
      {isLong && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-rose-700 active:text-rose-900 transition-colors self-start cursor-pointer whitespace-nowrap px-2 py-1.5 rounded-lg active:bg-rose-200 active:scale-[0.97]"
        >
          {expanded ? "閉じる ▲" : "続きを読む ▼"}
        </button>
      )}
    </div>
  );
}

export default function ReviewsSection() {
  const { ref: headRef, style: headStyle } = useScrollFadeIn<HTMLDivElement>();
  const { ref: gridRef, style: gridStyle } = useScrollFadeIn<HTMLDivElement>({ delay: 0.15 });

  return (
    <section id="reviews" className="py-20 md:py-14 bg-amber-200">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div ref={headRef} style={headStyle} className="text-center mb-5 md:mb-8">
          <p className="text-xs tracking-[0.4em] text-rose-900 uppercase mb-2 md:mb-3">Reviews</p>
          <h2 className="font-en text-3xl md:text-4xl font-normal text-stone-800 tracking-widest">
            Reviews
          </h2>
          <div className="w-12 h-0.5 bg-rose-600 mx-auto mt-4 md:mt-5"></div>
        </div>
        <div ref={gridRef} style={gridStyle} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {reviews.map((r) => <ReviewCard key={r.id} review={r} />)}
        </div>
      </div>
    </section>
  );
}
